/**
 * PayslipsOverview — Toutes les fiches de paie par salarié × période + bulk-upload + re-extract.
 */
import { useCallback, useEffect, useState } from "react";
import { PayslipPdfDialog } from "@/components/hr/PayslipPdfDialog";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ReceiptText, Upload, RefreshCw, Sparkles, Download, FileArchive, Eye } from "lucide-react";
import { toast } from "sonner";

const MONTHS = ["janvier","février","mars","avril","mai","juin","juillet","août","septembre","octobre","novembre","décembre"];
const fmtEUR = (v) => v == null ? "—" : `${Number(v).toLocaleString("fr-FR", { minimumFractionDigits: 2 })} €`;

export default function PayslipsOverview() {
  const { hasPermission } = useAuth();
  const canWrite = hasPermission("hr.write") || hasPermission("admin");
  const [year, setYear] = useState(new Date().getFullYear());
  const [rows, setRows] = useState([]);
  const [emps, setEmps] = useState([]);
  const [dialog, setDialog] = useState(false);
  const [period, setPeriod] = useState(new Date().toISOString().slice(0, 7));
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [report, setReport] = useState(null);
  const [viewing, setViewing] = useState(null);
  const closeViewer = useCallback(() => setViewing(null), []);

  const load = async () => {
    try {
      const [empR] = await Promise.all([api.get("/hr/employees")]);
      setEmps(empR.data.employees || []);
      // Aggregate: for each employee, fetch payslips
      const all = [];
      for (const e of (empR.data.employees || [])) {
        const r = await api.get(`/hr/employees/${e.id}/payslips`);
        (r.data.payslips || []).forEach(p => {
          if (p.period && p.period.startsWith(String(year))) {
            all.push({ ...p, employee_name: `${e.first_name} ${e.last_name}`, employee_id: e.id, job_title: e.job_title });
          }
        });
      }
      all.sort((a, b) => (b.period || "").localeCompare(a.period || ""));
      setRows(all);
    } catch (e) { toast.error(apiError(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [year]);

  const bulkUpload = async () => {
    if (files.length === 0) { toast.error("Sélectionnez des PDFs"); return; }
    setUploading(true);
    const fd = new FormData();
    fd.append("period", period);
    for (const f of files) fd.append("files", f);
    try {
      const r = await api.post("/hr/payslips/bulk-upload", fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      setReport(r.data);
      toast.success(`${r.data.matched} bulletin(s) importé(s) · ${r.data.unmatched.length} non-matchés`);
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setUploading(false); }
  };
  const reExtract = async (pid) => {
    try { await api.post(`/hr/payslips/${pid}/re-extract`); toast.success("Extraction relancée"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const exportCSV = async () => {
    try {
      const r = await api.get("/hr/exports/employees.csv", { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url; a.download = "salaries.csv"; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="payslips-overview-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: "Manrope, sans-serif" }}>
            Bulletins de paie
          </h1>
          <p className="text-sm text-slate-500">{rows.length} bulletins · année {year}</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={exportCSV} data-testid="btn-export-csv">
            <Download className="w-4 h-4 mr-1.5" /> Export CSV salariés
          </Button>
          {canWrite && (
            <Button onClick={() => setDialog(true)} className="bg-blue-600 hover:bg-blue-700 text-white"
              data-testid="btn-bulk-upload">
              <Upload className="w-4 h-4 mr-1.5" /> Import lot bulletins
            </Button>
          )}
        </div>
      </div>

      <Card>
        <CardContent className="p-4">
          <Select value={String(year)} onValueChange={(v) => setYear(parseInt(v))}>
            <SelectTrigger className="max-w-xs" data-testid="year-select"><SelectValue /></SelectTrigger>
            <SelectContent>{[year - 2, year - 1, year, year + 1].map(y =>
              <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
          </Select>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          {rows.length === 0 ? (
            <div className="p-12 text-center">
              <ReceiptText className="w-12 h-12 mx-auto text-slate-300 mb-3" />
              <p className="text-slate-500">Aucun bulletin.</p>
            </div>
          ) : (
            <Table>
              <TableHeader><TableRow className="bg-slate-50">
                <TableHead className="text-xs uppercase">Période</TableHead>
                <TableHead className="text-xs uppercase">Salarié</TableHead>
                <TableHead className="text-xs uppercase text-right">Brut</TableHead>
                <TableHead className="text-xs uppercase text-right">Net à payer</TableHead>
                <TableHead className="text-xs uppercase">Extraction</TableHead>
                <TableHead className="text-xs uppercase text-right">Actions</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {rows.map((p, i) => (
                  <TableRow key={p.id} className="border-b hover:bg-slate-50" data-testid={`payslip-ov-${i}`}>
                    <TableCell className="font-mono">
                      <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                        {p.period && `${MONTHS[parseInt(p.period.split("-")[1]) - 1]} ${p.period.split("-")[0]}`}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="font-medium text-sm">{p.employee_name}</div>
                      <div className="text-xs text-slate-500">{p.job_title || "—"}</div>
                    </TableCell>
                    <TableCell className="text-right font-mono">{fmtEUR(p.gross_salary)}</TableCell>
                    <TableCell className="text-right font-mono font-semibold">{fmtEUR(p.net_salary)}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className={
                        p.extraction_status === "ok" ? "bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px]" :
                        p.extraction_status === "manual" ? "bg-slate-100 text-[10px]" :
                        "bg-amber-50 text-amber-700 border-amber-200 text-[10px]"
                      }>
                        {p.extraction_status === "ok" ? "IA ✓" : p.extraction_status === "manual" ? "Manuel" : "IA échec"}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      {p.file_asset_id && (
                        <Button size="sm" variant="ghost" onClick={() => setViewing(p)}
                          className="h-7 text-xs text-blue-600" data-testid={`view-payslip-ov-${i}`}>
                          <Eye className="w-3.5 h-3.5 mr-1" /> Consulter
                        </Button>
                      )}
                      {canWrite && p.file_asset_id && (
                        <Button size="sm" variant="ghost" onClick={() => reExtract(p.id)}
                          className="h-7 text-xs" data-testid={`re-extract-${i}`}>
                          <Sparkles className="w-3.5 h-3.5 mr-1" /> Re-IA
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <PayslipPdfDialog payslip={viewing} onClose={closeViewer} />
      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent className="max-w-lg" data-testid="bulk-upload-dialog">
          <DialogHeader><DialogTitle>Import lot bulletins</DialogTitle><DialogDescription className="sr-only">Détails de la modale.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div><Label>Période (YYYY-MM)</Label>
              <Input value={period} onChange={(e) => setPeriod(e.target.value)} className="font-mono" />
            </div>
            <div><Label>Fichiers PDF (nom doit contenir le nom du salarié)</Label>
              <Input type="file" multiple accept="application/pdf"
                onChange={(e) => setFiles(Array.from(e.target.files || []))}
                data-testid="bulk-files" />
              <p className="text-xs text-slate-500 mt-1">
                Ex: `bulletin_dupont_2026-09.pdf` sera assigné à Dupont automatiquement.
              </p>
            </div>
            {report && (
              <div className="p-3 bg-slate-50 rounded text-sm">
                <div><b>{report.matched}</b> bulletins matchés</div>
                {report.unmatched.length > 0 && <div className="text-amber-700">Non-matchés : {report.unmatched.join(", ")}</div>}
                {report.errors.length > 0 && <div className="text-red-700">Erreurs : {report.errors.length}</div>}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setDialog(false); setReport(null); }}>Fermer</Button>
            <Button onClick={bulkUpload} disabled={uploading} className="bg-blue-600 hover:bg-blue-700 text-white"
              data-testid="btn-bulk-confirm">
              {uploading ? "Import..." : `Importer ${files.length} fichier(s)`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
